'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import { aiModelsApi, ImageGenImg2ImgResponse } from '@/lib/ai-models-api';

export default function ImageGenImg2ImgPage() {
  const [prompt, setPrompt] = useState('');
  const [inputImage, setInputImage] = useState<string | null>(null);
  const [inputImagePreview, setInputImagePreview] = useState<string | null>(null);
  const [steps, setSteps] = useState(50);
  const [seed, setSeed] = useState<number | null>(null);
  const [guidanceScale, setGuidanceScale] = useState(1.5);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImageGenImg2ImgResponse | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setInputImagePreview(previewUrl);

    // Convert to base64
    const base64 = await fileToBase64(file);
    setInputImage(base64);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setInputImagePreview(previewUrl);
      const base64 = await fileToBase64(file);
      setInputImage(base64);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || !inputImage) return;

    setLoading(true);
    setError(null);

    try {
      const response = await aiModelsApi.imagegenImg2Img(inputImage, {
        prompt: prompt.trim(),
        num_inference_steps: steps,
        guidance_scale: guidanceScale,
        seed,
        model: 'glm-image',
      });
      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">GLM-Image - Image to Image</h1>
          <span className="px-2 py-0.5 text-xs bg-[var(--accent)]/20 text-[var(--accent)] rounded">GLM-Image</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Input Image */}
                <div>
                  <label className="block text-sm font-medium mb-2">Input Image</label>
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                      inputImagePreview
                        ? 'border-[var(--accent)]'
                        : 'border-[var(--border-subtle)] hover:border-[var(--accent)]'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    {inputImagePreview ? (
                      <div className="relative">
                        <img
                          src={inputImagePreview}
                          alt="Input"
                          className="max-h-48 mx-auto rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setInputImage(null);
                            setInputImagePreview(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="py-8">
                        <svg className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-[var(--text-muted)]">Click or drag image to upload</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">Edit Prompt</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the changes you want to make..."
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none resize-none"
                    rows={4}
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Tip: Use quotes for text you want rendered, e.g. &quot;Hello World&quot;
                  </p>
                </div>

                {/* Steps */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Steps: {steps}
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="1"
                    value={steps}
                    onChange={(e) => setSteps(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <div className="flex justify-between text-xs text-[var(--text-muted)] mt-1">
                    <span>10 (Fast)</span>
                    <span>100 (Quality)</span>
                  </div>
                </div>

                {/* Guidance Scale */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Guidance Scale: {guidanceScale.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="5"
                    step="0.1"
                    value={guidanceScale}
                    onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <div className="flex justify-between text-xs text-[var(--text-muted)] mt-1">
                    <span>0.5</span>
                    <span>5.0</span>
                  </div>
                </div>

                {/* Seed */}
                <div>
                  <label className="block text-sm font-medium mb-2">Seed (optional)</label>
                  <input
                    type="number"
                    value={seed ?? ''}
                    onChange={(e) => setSeed(e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Random"
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!prompt.trim() || !inputImage || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating...' : 'Generate'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>

              {/* Examples */}
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h3 className="text-sm font-semibold mb-3">Example Prompts</h3>
                <div className="space-y-2 text-sm text-[var(--text-muted)]">
                  <p className="cursor-pointer hover:text-[var(--text-primary)]" onClick={() => setPrompt('Replace the background with a sunset beach scene')}>
                    • Replace the background with a sunset beach scene
                  </p>
                  <p className="cursor-pointer hover:text-[var(--text-primary)]" onClick={() => setPrompt('Add a "SALE 50% OFF" sign to the image')}>
                    • Add a &quot;SALE 50% OFF&quot; sign to the image
                  </p>
                  <p className="cursor-pointer hover:text-[var(--text-primary)]" onClick={() => setPrompt('Transform into anime style artwork')}>
                    • Transform into anime style artwork
                  </p>
                  <p className="cursor-pointer hover:text-[var(--text-primary)]" onClick={() => setPrompt('Make it look like a vintage photograph')}>
                    • Make it look like a vintage photograph
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Result</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image and describe your edits</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Generating image...</p>
                    <p className="text-xs text-[var(--text-muted)] mt-2">This may take up to 2 minutes</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Before/After Comparison */}
                    <div className="grid grid-cols-2 gap-4">
                      {inputImagePreview && (
                        <div>
                          <p className="text-xs text-[var(--text-muted)] mb-2 text-center">Original</p>
                          <img
                            src={inputImagePreview}
                            alt="Original"
                            className="w-full rounded-lg"
                          />
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-[var(--text-muted)] mb-2 text-center">Result</p>
                        <img
                          src={result.image.startsWith('data:') ? result.image : `data:image/png;base64,${result.image}`}
                          alt="Generated"
                          className="w-full rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Size</p>
                        <p className="text-lg font-semibold">{result.width}x{result.height}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Seed</p>
                        <p className="text-lg font-semibold font-mono">{result.seed ?? 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Time</p>
                        <p className="text-lg font-semibold">{(result.inference_time_ms / 1000).toFixed(1)}s</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <a
                        href={result.image.startsWith('data:') ? result.image : `data:image/png;base64,${result.image}`}
                        download="img2img-result.png"
                        className="flex-1 py-3 bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-center font-medium rounded-lg transition-colors border border-[var(--border-subtle)]"
                      >
                        Download
                      </a>
                      {result.seed && (
                        <button
                          onClick={() => setSeed(result.seed)}
                          className="flex-1 py-3 bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-center font-medium rounded-lg transition-colors border border-[var(--border-subtle)]"
                        >
                          Use Seed
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
